import React, { useEffect, useState } from 'react';
import {
  Mail, Linkedin, Phone, Copy, Check, Filter, ChevronDown, ChevronUp,
  Loader2, FileText, Users, Zap, Plus,
} from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', label: 'All Templates' },
  { id: 'client_acquisition', label: 'Client Acquisition' },
  { id: 'candidate_outreach', label: 'Candidate Outreach' },
  { id: 'warm_lead', label: 'Warm Lead Conversion' },
];

const CHANNELS = [
  { id: 'all', label: 'All', icon: FileText },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'phone', label: 'Phone', icon: Phone },
];

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); toast.success('Copied to clipboard'); setTimeout(() => setCopied(false), 2000); }}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-testid="copy-template">
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3 text-gray-500" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const ChannelBadge = ({ channel }) => {
  const config = {
    email: { icon: Mail, color: 'bg-blue-50 text-blue-700 border-blue-200' },
    linkedin: { icon: Linkedin, color: 'bg-sky-50 text-sky-700 border-sky-200' },
    phone: { icon: Phone, color: 'bg-green-50 text-green-700 border-green-200' },
  };
  const c = config[channel] || config.email;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${c.color}`}>
      <c.icon className="h-3 w-3" />{channel}
    </span>
  );
};

const CategoryLabel = ({ cat }) => {
  const labels = { client_acquisition: 'Client Acquisition', candidate_outreach: 'Candidate Outreach', warm_lead: 'Warm Lead', custom: 'Custom' };
  return <span className="text-[10px] text-gray-400 uppercase tracking-wider">{labels[cat] || cat}</span>;
};

const TemplateCard = ({ template, expanded, onToggle }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors" data-testid="template-card">
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-3.5 text-left">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <CategoryLabel cat={template.category} />
          <ChannelBadge channel={template.channel} />
        </div>
        <h3 className="text-sm font-semibold text-[#0A1F44] truncate">{template.name}</h3>
      </div>
      <div className="flex items-center gap-2 ml-4">
        {template.dynamic_fields?.length > 0 && (
          <span className="text-[10px] text-gray-400">{template.dynamic_fields.length} fields</span>
        )}
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </div>
    </button>
    {expanded && (
      <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
        <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed mb-3" data-testid="template-text">{template.text}</pre>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {(template.dynamic_fields || []).map(f => (
              <span key={f} className="px-2 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700 border border-amber-200 font-mono">{`{{${f}}}`}</span>
            ))}
          </div>
          <CopyBtn text={template.text} />
        </div>
      </div>
    )}
  </div>
);

const OutreachTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [channel, setChannel] = useState('all');
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (channel !== 'all') params.set('channel', channel);
    fetchWithAuth(`${API_URL}/api/templates?${params}`)
      .then(d => setTemplates(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, channel]);

  const emailCount = templates.filter(t => t.channel === 'email').length;
  const linkedinCount = templates.filter(t => t.channel === 'linkedin').length;
  const phoneCount = templates.filter(t => t.channel === 'phone').length;

  return (
    <IntelligenceLayout title="Outreach Templates">
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="templates-title">Outreach Template Library</h1>
        <p className="text-sm text-gray-500">Reusable templates for client acquisition, candidate outreach, and warm lead conversion</p>
      </header>

      <main className="p-8 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Loading templates...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="template-metrics">
              <div onClick={() => { setChannel('all'); setExpandedIdx(null); }} className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${channel === 'all' ? 'border-[#0A1F44] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="text-xs text-gray-500 mb-1">Total Templates</div>
                <div className="text-2xl font-bold text-[#0A1F44]">{templates.length}</div>
              </div>
              <div onClick={() => { setChannel('email'); setExpandedIdx(null); }} className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${channel === 'email' ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1"><Mail className="h-3 w-3" />Email</div>
                <div className="text-2xl font-bold text-blue-600">{emailCount}</div>
              </div>
              <div onClick={() => { setChannel('linkedin'); setExpandedIdx(null); }} className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${channel === 'linkedin' ? 'border-sky-500 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1"><Linkedin className="h-3 w-3" />LinkedIn</div>
                <div className="text-2xl font-bold text-sky-600">{linkedinCount}</div>
              </div>
              <div onClick={() => { setChannel('phone'); setExpandedIdx(null); }} className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${channel === 'phone' ? 'border-green-500 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1"><Phone className="h-3 w-3" />Phone Scripts</div>
                <div className="text-2xl font-bold text-green-600">{phoneCount}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3" data-testid="template-filters">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-600 font-medium">Category:</span>
              </div>
              {CATEGORIES.map(c => (
                <button key={c.id} onClick={() => { setCategory(c.id); setExpandedIdx(null); }} data-testid={`cat-${c.id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${category === c.id ? 'bg-[#D4AF37] text-[#0A1F44] shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  {c.label}
                </button>
              ))}
              <span className="text-gray-300 mx-1">|</span>
              {CHANNELS.map(ch => (
                <button key={ch.id} onClick={() => { setChannel(ch.id); setExpandedIdx(null); }} data-testid={`ch-${ch.id}`}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${channel === ch.id ? 'bg-[#0A1F44] text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  <ch.icon className="h-3 w-3" />{ch.label}
                </button>
              ))}
            </div>

            <div className="space-y-2" data-testid="template-list">
              {templates.length === 0 ? (
                <div className="text-center py-12 text-gray-400">No templates match the current filters.</div>
              ) : templates.map((t, i) => (
                <TemplateCard key={t.name} template={t} expanded={expandedIdx === i} onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)} />
              ))}
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default OutreachTemplates;
